import { CuttingShape } from '@/domain/cuttingShape/CuttingShape';
import type { IProductImageRepository } from '@/application/ports/IProductImageRepository';

// Per-uuid memoisation of the fetched shape. Both sides ask for it in parallel at mount;
// the first triggers the fetch, the others await the same Promise → one network roundtrip
// and one blob URL shared across sides. Explicit invalidation on delete keeps stale
// entries from leaking.
const _shapeByUuid = new Map<string, Promise<CuttingShape | undefined>>();

async function fetchCuttingShapeFile(
    productImageRepository: IProductImageRepository,
    cuttingPathUuid: string | null,
): Promise<File | undefined> {
    if (!cuttingPathUuid) {
        return undefined;
    }
    const cuttingPathBlob = await productImageRepository.get(cuttingPathUuid);
    return new File([cuttingPathBlob], 'cutting-path', { type: cuttingPathBlob.type });
}

function createCuttingShape(file: File, cuttingPathUuid: string): CuttingShape {
    return new CuttingShape({ url: URL.createObjectURL(file), uuid: cuttingPathUuid });
}

function loadCuttingShape(productImageRepository: IProductImageRepository, cuttingPathUuid: string): Promise<CuttingShape | undefined> {
    const cached = _shapeByUuid.get(cuttingPathUuid);
    if (cached) {
        return cached;
    }
    const promise = (async () => {
        const file = await fetchCuttingShapeFile(productImageRepository, cuttingPathUuid);
        return file ? createCuttingShape(file, cuttingPathUuid) : undefined;
    })();
    _shapeByUuid.set(cuttingPathUuid, promise);
    return promise;
}

function invalidateCuttingShape(cuttingPathUuid: string): void {
    _shapeByUuid.delete(cuttingPathUuid);
}

function primeCuttingShape(shape: CuttingShape): void {
    _shapeByUuid.set(shape.uuid, Promise.resolve(shape));
}

export { createCuttingShape, fetchCuttingShapeFile, invalidateCuttingShape, loadCuttingShape, primeCuttingShape };
